import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Activity,
  BarChart3,
  CreditCard,
  Loader2,
  Mail,
  Search,
  ShieldAlert,
  ShieldCheck,
  Sparkles,
  Users as UsersIcon,
  CheckCircle2,
  XCircle,
  Trash2,
  Ban,
  Crown,
  Send,
} from "lucide-react";
import { toast } from "sonner";

import { apiClient } from "../lib/api";
import { useAuth, startLogin } from "../lib/auth";

const wc = { withCredentials: true };

function StatCard({ label, value, sub, accent = false, testId }) {
  return (
    <div
      data-testid={testId}
      className={`border border-black p-5 ${accent ? "bg-[#0047FF] text-white" : "bg-white"}`}
    >
      <div className="font-mono text-[10px] uppercase tracking-widest opacity-70 mb-2">
        {label}
      </div>
      <div className="font-display text-3xl tracking-tight leading-none">
        {value}
      </div>
      {sub ? (
        <div className={`text-xs mt-2 ${accent ? "opacity-80" : "text-[#475569]"}`}>
          {sub}
        </div>
      ) : null}
    </div>
  );
}

function SectionHeader({ kicker, title, action }) {
  return (
    <div className="flex items-end justify-between mb-5">
      <div>
        <div className="font-mono text-[11px] uppercase tracking-widest text-[#0047FF] mb-1">
          {kicker}
        </div>
        <h2 className="font-display text-2xl tracking-tight">{title}</h2>
      </div>
      {action}
    </div>
  );
}

// ============= OVERVIEW =============
function Overview() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    apiClient
      .get("/admin/overview", wc)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Failed to load"));
  }, []);

  if (err) return <div className="text-sm text-red-600">{err}</div>;
  if (!data)
    return (
      <div className="flex items-center gap-2 text-sm text-[#475569]">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading analytics…
      </div>
    );

  const maxScan = Math.max(1, ...(data.scans.daily_series_14d || []).map((d) => d.count));

  return (
    <div className="space-y-8" data-testid="admin-overview">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          testId="stat-users"
          label="Users"
          value={data.users.total.toLocaleString()}
          sub={`+${data.users.new_7d} in last 7d`}
        />
        <StatCard
          testId="stat-scans"
          label="Total Scans"
          value={data.scans.total.toLocaleString()}
          sub={`${data.scans.last_24h} in last 24h · ${data.scans.last_7d} in last 7d`}
        />
        <StatCard
          testId="stat-revenue"
          label="Revenue · all time"
          value={`$${data.revenue.total_usd.toFixed(2)}`}
          sub={`${data.revenue.paid_transactions} paid · $${data.revenue.last_30d_usd.toFixed(2)} last 30d`}
          accent
        />
        <StatCard
          testId="stat-llm"
          label="LLM Spend · 30d"
          value={`$${data.llm.spend_30d_usd.toFixed(2)}`}
          sub={`${data.llm.calls_30d} calls · $${data.llm.spend_all_time_usd.toFixed(2)} all time`}
        />
      </div>

      <div className="border border-black bg-white p-6">
        <SectionHeader kicker="/ Daily Scans" title="Last 14 days" />
        {data.scans.daily_series_14d?.length ? (
          <div className="flex items-end gap-2 h-40">
            {data.scans.daily_series_14d.map((d) => (
              <div key={d.day} className="flex-1 flex flex-col items-center gap-1">
                <div className="text-[10px] font-mono text-[#475569]">{d.count}</div>
                <div
                  className="w-full bg-[#0047FF]"
                  style={{ height: `${(d.count / maxScan) * 100}%`, minHeight: 2 }}
                  title={`${d.day} — ${d.count}`}
                />
                <div className="text-[9px] font-mono text-[#475569] rotate-45 origin-left translate-y-2">
                  {d.day.slice(5)}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-sm text-[#475569]">No scans in this window.</div>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="border border-black p-5">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#475569] mb-2">
            Pro Unlocked Scans
          </div>
          <div className="font-display text-3xl">{data.scans.pro_unlocked}</div>
        </div>
        <div className="border border-black p-5">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#475569] mb-2">
            Waitlist Signups
          </div>
          <div className="font-display text-3xl">{data.waitlist}</div>
        </div>
      </div>
    </div>
  );
}

// ============= USERS =============
function Users() {
  const [users, setUsers] = useState(null);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(null);

  const load = (q = "") => {
    apiClient
      .get("/admin/users", { ...wc, params: { search: q, limit: 200 } })
      .then((r) => setUsers(r.data.users || []))
      .catch(() => setUsers([]));
  };
  useEffect(() => {
    load();
  }, []);

  const act = async (uid, action, label) => {
    if (action === "delete" && !window.confirm(`Delete user ${label}? Their scans become orphaned.`)) return;
    if (action === "ban" && !window.confirm(`Ban ${label}? They will be signed out and blocked.`)) return;
    setBusy(uid + action);
    try {
      await apiClient.post(`/admin/users/${uid}/action`, { action }, wc);
      toast.success(`${action} → ${label}`);
      load(search);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div data-testid="admin-users">
      <SectionHeader
        kicker="/ Users"
        title="Member roster"
        action={
          <div className="flex items-center gap-2 border border-black px-3 py-2 bg-white">
            <Search className="w-4 h-4 text-[#475569]" />
            <input
              data-testid="users-search"
              className="outline-none text-sm w-48 bg-transparent"
              placeholder="Search email…"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                load(e.target.value);
              }}
            />
          </div>
        }
      />

      {!users ? (
        <div className="flex items-center gap-2 text-sm text-[#475569]">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading users…
        </div>
      ) : (
        <div className="border border-black bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F8F9FA] font-mono text-[10px] uppercase tracking-widest text-[#475569]">
              <tr>
                <th className="text-left px-4 py-3">Email</th>
                <th className="text-left px-4 py-3">Joined</th>
                <th className="text-right px-4 py-3">Scans</th>
                <th className="text-right px-4 py-3">Pro</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-[#475569]">
                    No users yet.
                  </td>
                </tr>
              ) : (
                users.map((u) => (
                  <tr key={u.user_id} className="border-t border-[#E2E8F0]" data-testid={`user-row-${u.email}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-[#0A0A0A]">{u.email}</div>
                      <div className="text-xs text-[#475569]">{u.name}</div>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">
                      {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                    </td>
                    <td className="px-4 py-3 text-right font-mono">{u.scans_count}</td>
                    <td className="px-4 py-3 text-right font-mono">{u.pro_count}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {u.is_admin && (
                          <span className="px-1.5 py-0.5 bg-[#0047FF] text-white font-mono text-[10px] uppercase tracking-widest">
                            Admin
                          </span>
                        )}
                        {u.banned && (
                          <span className="px-1.5 py-0.5 bg-red-100 text-red-700 font-mono text-[10px] uppercase tracking-widest">
                            Banned
                          </span>
                        )}
                        {!u.is_admin && !u.banned && (
                          <span className="text-xs text-[#475569]">—</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        <button
                          data-testid={`promote-${u.email}`}
                          disabled={busy === u.user_id + "promote" || u.is_admin}
                          onClick={() => act(u.user_id, "promote", u.email)}
                          className="px-2 py-1 text-xs border border-black hover:bg-[#0047FF] hover:text-white disabled:opacity-40"
                          title="Promote to admin"
                        >
                          <Crown className="w-3.5 h-3.5" />
                        </button>
                        <button
                          data-testid={`demote-${u.email}`}
                          disabled={busy === u.user_id + "demote" || !u.is_admin}
                          onClick={() => act(u.user_id, "demote", u.email)}
                          className="px-2 py-1 text-xs border border-black hover:bg-[#0A0A0A] hover:text-white disabled:opacity-40"
                          title="Demote admin"
                        >
                          <ShieldCheck className="w-3.5 h-3.5" />
                        </button>
                        {u.banned ? (
                          <button
                            data-testid={`unban-${u.email}`}
                            disabled={busy === u.user_id + "unban"}
                            onClick={() => act(u.user_id, "unban", u.email)}
                            className="px-2 py-1 text-xs border border-black hover:bg-[#10B981] hover:text-white"
                            title="Unban"
                          >
                            <CheckCircle2 className="w-3.5 h-3.5" />
                          </button>
                        ) : (
                          <button
                            data-testid={`ban-${u.email}`}
                            disabled={busy === u.user_id + "ban"}
                            onClick={() => act(u.user_id, "ban", u.email)}
                            className="px-2 py-1 text-xs border border-black hover:bg-red-500 hover:text-white"
                            title="Ban"
                          >
                            <Ban className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <button
                          data-testid={`delete-${u.email}`}
                          disabled={busy === u.user_id + "delete"}
                          onClick={() => act(u.user_id, "delete", u.email)}
                          className="px-2 py-1 text-xs border border-black hover:bg-red-700 hover:text-white"
                          title="Delete"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ============= PAYMENTS =============
function Payments() {
  const [items, setItems] = useState(null);

  useEffect(() => {
    apiClient
      .get("/admin/payments", { ...wc, params: { limit: 200 } })
      .then((r) => setItems(r.data.payments || []))
      .catch(() => setItems([]));
  }, []);

  const totals = useMemo(() => {
    if (!items) return { paid: 0, pending: 0, total: 0 };
    let paid = 0,
      pending = 0;
    for (const t of items) {
      if (t.payment_status === "paid") paid += Number(t.amount || 0);
      else pending += Number(t.amount || 0);
    }
    return { paid, pending, total: paid + pending };
  }, [items]);

  return (
    <div data-testid="admin-payments">
      <SectionHeader kicker="/ Payments" title="Transactions" />
      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        <StatCard label="Paid total" value={`$${totals.paid.toFixed(2)}`} accent />
        <StatCard label="Pending" value={`$${totals.pending.toFixed(2)}`} />
        <StatCard label="Transactions" value={(items?.length || 0).toString()} />
      </div>
      {!items ? (
        <div className="flex items-center gap-2 text-sm text-[#475569]">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading payments…
        </div>
      ) : items.length === 0 ? (
        <div className="text-sm text-[#475569]">No transactions yet.</div>
      ) : (
        <div className="border border-black bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F8F9FA] font-mono text-[10px] uppercase tracking-widest text-[#475569]">
              <tr>
                <th className="text-left px-4 py-3">Created</th>
                <th className="text-left px-4 py-3">Domain</th>
                <th className="text-left px-4 py-3">Session ID</th>
                <th className="text-right px-4 py-3">Amount</th>
                <th className="text-left px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map((t) => (
                <tr key={t.session_id} className="border-t border-[#E2E8F0]">
                  <td className="px-4 py-3 font-mono text-xs">
                    {t.created_at ? new Date(t.created_at).toLocaleString() : "—"}
                  </td>
                  <td className="px-4 py-3">{t.domain || "—"}</td>
                  <td className="px-4 py-3 font-mono text-xs truncate max-w-[200px]">
                    {t.session_id}
                  </td>
                  <td className="px-4 py-3 text-right font-mono">
                    ${Number(t.amount || 0).toFixed(2)} {(t.currency || "usd").toUpperCase()}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-widest ${
                        t.payment_status === "paid"
                          ? "bg-[#10B981] text-white"
                          : "bg-amber-100 text-amber-800"
                      }`}
                    >
                      {t.payment_status || "pending"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ============= STRIPE SETTINGS =============
function StripeSettings() {
  const [data, setData] = useState(null);
  const [newKey, setNewKey] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = () =>
    apiClient
      .get("/admin/settings/stripe", wc)
      .then((r) => setData(r.data))
      .catch(() => setData({}));

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await apiClient.put("/admin/settings/stripe", { stripe_api_key: newKey || null }, wc);
      toast.success("Stripe settings saved");
      setNewKey("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const testConn = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await apiClient.post("/admin/settings/stripe/test", null, wc);
      setTestResult({ ok: true, ...r.data });
      toast.success("Stripe connection OK");
    } catch (e) {
      setTestResult({ ok: false, error: e?.response?.data?.detail || "Test failed" });
      toast.error("Stripe test failed");
    } finally {
      setTesting(false);
    }
  };

  if (!data)
    return (
      <div className="flex items-center gap-2 text-sm text-[#475569]">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading…
      </div>
    );

  return (
    <div data-testid="admin-stripe" className="max-w-xl space-y-6">
      <SectionHeader kicker="/ Stripe" title="Payment Provider Keys" />
      <div className="border border-black bg-white p-5 space-y-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#475569] mb-1">
            Current key
          </div>
          <div className="font-mono text-sm">
            {data.stripe_api_key_masked || "— not configured —"}
          </div>
          <div className="text-xs text-[#475569] mt-1">
            Mode: <strong>{data.stripe_mode}</strong> · Source:{" "}
            <strong>{data.source}</strong>
          </div>
        </div>
        <div>
          <label className="font-mono text-[10px] uppercase tracking-widest text-[#475569] block mb-1">
            New Stripe Secret Key
          </label>
          <input
            data-testid="stripe-key-input"
            type="password"
            value={newKey}
            onChange={(e) => setNewKey(e.target.value)}
            placeholder="sk_live_... or sk_test_..."
            className="w-full border border-black px-3 py-2 font-mono text-sm outline-none focus:border-[#0047FF]"
          />
        </div>
        <div className="flex gap-2">
          <button
            data-testid="save-stripe-btn"
            disabled={saving || !newKey}
            onClick={save}
            className="pl-btn-primary px-4 py-2 text-sm border border-black disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin inline" /> : "Save key"}
          </button>
          <button
            data-testid="test-stripe-btn"
            disabled={testing || !data.configured}
            onClick={testConn}
            className="pl-btn-ghost px-4 py-2 text-sm disabled:opacity-50"
          >
            {testing ? <Loader2 className="w-4 h-4 animate-spin inline" /> : "Test connection"}
          </button>
        </div>
        {testResult && (
          <div
            className={`p-3 border ${
              testResult.ok ? "border-[#10B981] bg-[#F0FDF4]" : "border-red-500 bg-red-50"
            } text-sm`}
          >
            {testResult.ok ? (
              <div>
                <CheckCircle2 className="w-4 h-4 inline mr-1 text-[#10B981]" />
                Connected to Stripe ({testResult.mode}). Balance:{" "}
                {(testResult.available || []).map((b) =>
                  `${(b.amount / 100).toFixed(2)} ${b.currency.toUpperCase()}`
                ).join(", ") || "—"}
              </div>
            ) : (
              <div>
                <XCircle className="w-4 h-4 inline mr-1 text-red-600" />
                {testResult.error}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ============= EMAIL / SMTP =============
function EmailSettings() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);

  const load = () =>
    apiClient
      .get("/admin/settings/email", wc)
      .then((r) => {
        setData(r.data);
        setForm({
          email_provider: r.data.provider || "smtp",
          smtp_host: r.data.smtp_host || "",
          smtp_port: r.data.smtp_port || 587,
          smtp_username: r.data.smtp_username || "",
          smtp_use_tls: r.data.smtp_use_tls !== false,
          sender_email: r.data.sender_email || "",
        });
      })
      .catch(() => setData({}));

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      // Build a clean body — strip empty strings so we don't accidentally
      // overwrite saved values with blanks. Numeric port coerced explicitly.
      const body = {};
      const stringFields = [
        "email_provider",
        "smtp_host",
        "smtp_username",
        "sender_email",
      ];
      for (const f of stringFields) {
        const v = (form[f] || "").trim();
        if (v) body[f] = v;
      }
      if (form.smtp_port) body.smtp_port = Number(form.smtp_port);
      if (typeof form.smtp_use_tls === "boolean") body.smtp_use_tls = form.smtp_use_tls;
      // Secrets only when explicitly typed
      if (form.smtp_password) body.smtp_password = form.smtp_password;
      if (form.resend_api_key) body.resend_api_key = form.resend_api_key;

      if (Object.keys(body).length === 0) {
        toast.error("Nothing to save — fill at least one field");
        return;
      }
      const r = await apiClient.put("/admin/settings/email", body, wc);
      // Use the echoed `saved` payload from the server so the form
      // immediately reflects the new state without a separate GET roundtrip.
      const saved = r.data?.saved || null;
      if (saved) {
        setData(saved);
        setForm({
          email_provider: saved.provider || "smtp",
          smtp_host: saved.smtp_host || "",
          smtp_port: saved.smtp_port || 587,
          smtp_username: saved.smtp_username || "",
          smtp_use_tls: saved.smtp_use_tls !== false,
          sender_email: saved.sender_email || "",
          smtp_password: "",
          resend_api_key: "",
        });
      } else {
        await load();
      }
      toast.success("Email settings saved");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d.msg || d.message || JSON.stringify(d)).join(" · ")
        : detail || "Save failed";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const testEmail = async () => {
    if (!testTo) {
      toast.error("Enter a recipient email");
      return;
    }
    setTesting(true);
    try {
      const r = await apiClient.post("/admin/settings/email/test", { to: testTo }, wc);
      toast.success(`Test sent via ${r.data.provider} to ${r.data.sent_to}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed");
    } finally {
      setTesting(false);
    }
  };

  if (!data)
    return (
      <div className="flex items-center gap-2 text-sm text-[#475569]">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading…
      </div>
    );

  const isSmtp = (form.email_provider || "smtp") === "smtp";

  return (
    <div data-testid="admin-email" className="max-w-2xl space-y-6">
      <SectionHeader kicker="/ Email" title="SMTP / Email Provider" />

      <div className="border border-black bg-white p-5 space-y-4">
        <div>
          <label className="font-mono text-[10px] uppercase tracking-widest text-[#475569] block mb-1">
            Provider
          </label>
          <div className="flex gap-3">
            {[
              { v: "smtp", l: "SMTP (recommended)" },
              { v: "resend", l: "Resend (HTTP API)" },
            ].map((opt) => (
              <label
                key={opt.v}
                className={`flex items-center gap-2 border border-black px-3 py-2 cursor-pointer ${
                  form.email_provider === opt.v ? "bg-black text-white" : "bg-white"
                }`}
              >
                <input
                  type="radio"
                  className="hidden"
                  data-testid={`provider-${opt.v}`}
                  checked={form.email_provider === opt.v}
                  onChange={() => setForm((f) => ({ ...f, email_provider: opt.v }))}
                />
                {opt.l}
              </label>
            ))}
          </div>
        </div>

        <div>
          <label className="font-mono text-[10px] uppercase tracking-widest text-[#475569] block mb-1">
            Sender (From)
          </label>
          <input
            data-testid="sender-email-input"
            value={form.sender_email || ""}
            onChange={(e) => setForm((f) => ({ ...f, sender_email: e.target.value }))}
            placeholder="hello@yourdomain.com"
            className="w-full border border-black px-3 py-2 text-sm outline-none focus:border-[#0047FF]"
          />
        </div>

        {isSmtp ? (
          <div className="space-y-3">
            <div className="grid sm:grid-cols-3 gap-3">
              <div className="sm:col-span-2">
                <label className="font-mono text-[10px] uppercase tracking-widest text-[#475569] block mb-1">
                  SMTP Host
                </label>
                <input
                  data-testid="smtp-host"
                  value={form.smtp_host || ""}
                  onChange={(e) => setForm((f) => ({ ...f, smtp_host: e.target.value }))}
                  placeholder="smtp.your-provider.com"
                  className="w-full border border-black px-3 py-2 text-sm outline-none focus:border-[#0047FF]"
                />
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-[#475569] block mb-1">
                  Port
                </label>
                <input
                  data-testid="smtp-port"
                  type="number"
                  value={form.smtp_port || 587}
                  onChange={(e) => setForm((f) => ({ ...f, smtp_port: e.target.value }))}
                  className="w-full border border-black px-3 py-2 text-sm outline-none focus:border-[#0047FF]"
                />
              </div>
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-[#475569] block mb-1">
                Username
              </label>
              <input
                data-testid="smtp-username"
                value={form.smtp_username || ""}
                onChange={(e) => setForm((f) => ({ ...f, smtp_username: e.target.value }))}
                className="w-full border border-black px-3 py-2 text-sm outline-none focus:border-[#0047FF]"
              />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-[#475569] block mb-1">
                Password
                {data.smtp_password_masked && (
                  <span className="ml-2 font-mono normal-case text-[#475569]">
                    current: {data.smtp_password_masked}
                  </span>
                )}
              </label>
              <input
                data-testid="smtp-password"
                type="password"
                value={form.smtp_password || ""}
                onChange={(e) => setForm((f) => ({ ...f, smtp_password: e.target.value }))}
                placeholder="Leave blank to keep current"
                className="w-full border border-black px-3 py-2 text-sm outline-none focus:border-[#0047FF]"
              />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                data-testid="smtp-use-tls"
                checked={form.smtp_use_tls !== false}
                onChange={(e) => setForm((f) => ({ ...f, smtp_use_tls: e.target.checked }))}
              />
              Use STARTTLS (recommended for port 587)
            </label>
          </div>
        ) : (
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-[#475569] block mb-1">
              Resend API Key
              {data.resend_api_key_masked && (
                <span className="ml-2 font-mono normal-case text-[#475569]">
                  current: {data.resend_api_key_masked}
                </span>
              )}
            </label>
            <input
              data-testid="resend-key"
              type="password"
              value={form.resend_api_key || ""}
              onChange={(e) => setForm((f) => ({ ...f, resend_api_key: e.target.value }))}
              placeholder="re_..."
              className="w-full border border-black px-3 py-2 font-mono text-sm outline-none focus:border-[#0047FF]"
            />
          </div>
        )}

        <button
          data-testid="save-email-btn"
          onClick={save}
          disabled={saving}
          className="pl-btn-primary px-4 py-2 text-sm border border-black disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin inline" /> : "Save email settings"}
        </button>
      </div>

      <div className="border border-black bg-white p-5">
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#475569] mb-2">
          Send test email
        </div>
        <div className="flex gap-2">
          <input
            data-testid="test-to"
            type="email"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            placeholder="you@yourdomain.com"
            className="flex-1 border border-black px-3 py-2 text-sm outline-none focus:border-[#0047FF]"
          />
          <button
            data-testid="test-email-btn"
            onClick={testEmail}
            disabled={testing}
            className="pl-btn-ghost px-4 py-2 text-sm"
          >
            {testing ? <Loader2 className="w-4 h-4 animate-spin inline" /> : "Send test"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============= LLM USAGE =============
function LlmUsage() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);

  useEffect(() => {
    setData(null);
    apiClient
      .get("/admin/llm-usage", { ...wc, params: { days } })
      .then((r) => setData(r.data))
      .catch(() => setData({}));
  }, [days]);

  if (!data)
    return (
      <div className="flex items-center gap-2 text-sm text-[#475569]">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading…
      </div>
    );

  const maxCost = Math.max(0.0001, ...(data.daily || []).map((d) => d.cost));

  return (
    <div data-testid="admin-llm" className="space-y-8">
      <SectionHeader
        kicker="/ LLM Usage"
        title="AI cost analytics"
        action={
          <div className="flex border border-black">
            {[7, 30, 90].map((n) => (
              <button
                key={n}
                onClick={() => setDays(n)}
                data-testid={`llm-days-${n}`}
                className={`px-3 py-1.5 text-xs font-mono uppercase tracking-widest ${
                  days === n ? "bg-[#0047FF] text-white" : "hover:bg-[#F1F5F9]"
                }`}
              >
                {n}d
              </button>
            ))}
          </div>
        }
      />

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Spend"
          value={`$${(data.totals?.cost_usd || 0).toFixed(4)}`}
          accent
        />
        <StatCard label="Calls" value={(data.totals?.calls || 0).toLocaleString()} />
        <StatCard
          label="Input tokens"
          value={(data.totals?.input_tokens || 0).toLocaleString()}
        />
        <StatCard
          label="Output tokens"
          value={(data.totals?.output_tokens || 0).toLocaleString()}
        />
      </div>

      <div className="border border-black bg-white p-6">
        <SectionHeader kicker="/ Daily" title={`Spend across ${days} days`} />
        {data.daily?.length ? (
          <div className="flex items-end gap-1 h-40">
            {data.daily.map((d) => (
              <div key={d.day} className="flex-1 flex flex-col items-center gap-1">
                <div className="text-[9px] font-mono text-[#475569]">
                  ${d.cost.toFixed(2)}
                </div>
                <div
                  className="w-full bg-[#0047FF]"
                  style={{ height: `${(d.cost / maxCost) * 100}%`, minHeight: 2 }}
                  title={`${d.day} — $${d.cost.toFixed(4)} (${d.calls} calls)`}
                />
                <div className="text-[8px] font-mono text-[#475569] truncate w-full text-center">
                  {d.day.slice(5)}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-sm text-[#475569]">No LLM usage in this window.</div>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="border border-black bg-white p-5">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#475569] mb-3">
            By Purpose
          </div>
          <table className="w-full text-sm">
            <tbody>
              {(data.by_purpose || []).map((p) => (
                <tr key={p.purpose} className="border-t border-[#E2E8F0]">
                  <td className="py-2">{p.purpose}</td>
                  <td className="py-2 text-right font-mono">{p.calls}</td>
                  <td className="py-2 text-right font-mono">${p.cost.toFixed(4)}</td>
                </tr>
              ))}
              {!data.by_purpose?.length && (
                <tr>
                  <td className="py-3 text-sm text-[#475569]">No data.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="border border-black bg-white p-5">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#475569] mb-3">
            By Model
          </div>
          <table className="w-full text-sm">
            <tbody>
              {(data.by_model || []).map((p) => (
                <tr key={p.model} className="border-t border-[#E2E8F0]">
                  <td className="py-2 font-mono text-xs">{p.model}</td>
                  <td className="py-2 text-right font-mono">{p.calls}</td>
                  <td className="py-2 text-right font-mono">${p.cost.toFixed(4)}</td>
                </tr>
              ))}
              {!data.by_model?.length && (
                <tr>
                  <td className="py-3 text-sm text-[#475569]">No data.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="border border-black bg-white">
        <div className="px-5 py-3 font-mono text-[10px] uppercase tracking-widest text-[#475569] border-b border-[#E2E8F0]">
          Recent calls
        </div>
        <table className="w-full text-sm">
          <thead className="bg-[#F8F9FA] font-mono text-[10px] uppercase tracking-widest text-[#475569]">
            <tr>
              <th className="text-left px-4 py-3">When</th>
              <th className="text-left px-4 py-3">Purpose</th>
              <th className="text-left px-4 py-3">Scan</th>
              <th className="text-right px-4 py-3">In</th>
              <th className="text-right px-4 py-3">Out</th>
              <th className="text-right px-4 py-3">Cost</th>
            </tr>
          </thead>
          <tbody>
            {(data.recent || []).map((r, i) => (
              <tr key={i} className="border-t border-[#E2E8F0]">
                <td className="px-4 py-2 font-mono text-xs">
                  {r.created_at ? new Date(r.created_at).toLocaleString() : "—"}
                </td>
                <td className="px-4 py-2">{r.purpose}</td>
                <td className="px-4 py-2 font-mono text-xs truncate max-w-[140px]">
                  {r.scan_id || "—"}
                </td>
                <td className="px-4 py-2 text-right font-mono">{r.input_tokens}</td>
                <td className="px-4 py-2 text-right font-mono">{r.output_tokens}</td>
                <td className="px-4 py-2 text-right font-mono">
                  ${(r.cost_usd || 0).toFixed(6)}
                </td>
              </tr>
            ))}
            {!data.recent?.length && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-[#475569]">
                  No LLM calls logged yet — run a free scan to populate this view.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============= TAKEDOWN STATS (purge audit line) =============
function timeAgo(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const secs = Math.max(1, Math.floor((Date.now() - t) / 1000));
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
}

function TakedownStatsLine({ stats }) {
  if (!stats) return null;
  const { purged_this_month = 0, total_purged = 0, last_purge, repeat_offenders = [] } = stats;
  return (
    <div
      data-testid="takedown-stats"
      className="border border-black bg-[#0A0A0A] text-white px-4 py-3 mb-4 font-mono text-xs uppercase tracking-widest flex flex-wrap items-center gap-x-6 gap-y-1"
    >
      <span>
        <span className="text-[#10B981]">{purged_this_month}</span> scan
        {purged_this_month === 1 ? "" : "s"} purged this month
      </span>
      <span className="text-[#94A3B8]">
        {total_purged} all-time
      </span>
      {last_purge ? (
        <span data-testid="takedown-last-purge">
          last purge:{" "}
          <span className="text-[#0047FF]">{last_purge.domain}</span> &middot;{" "}
          {timeAgo(last_purge.deleted_at)} &middot; by {last_purge.by}
        </span>
      ) : (
        <span className="text-[#94A3B8]">no purges recorded yet</span>
      )}
      {repeat_offenders.length > 0 && (
        <span
          data-testid="takedown-repeat-offenders"
          className="text-[#F59E0B]"
          title={repeat_offenders.map((r) => `${r.domain}: ${r.count}\u00d7`).join("\n")}
        >
          {"\u26a0"} {repeat_offenders.length} repeat-purge
          {repeat_offenders.length === 1 ? "" : "s"} (30d) &mdash; hover for list
        </span>
      )}
    </div>
  );
}


// ============= SCAN PINNING (verified score override) =============
function ScansPin() {
  const [search, setSearch] = useState("");
  const [scans, setScans] = useState(null);
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(null);
  const [pinFor, setPinFor] = useState(null); // domain
  const [pinScore, setPinScore] = useState(95);
  const [pinReason, setPinReason] = useState("Verified by PrivacyLens compliance team");

  const load = (q = "") =>
    apiClient
      .get("/admin/scans", { ...wc, params: { search: q, limit: 200 } })
      .then((r) => setScans(r.data.scans || []))
      .catch(() => setScans([]));

  const loadStats = () =>
    apiClient
      .get("/admin/takedown-stats", wc)
      .then((r) => setStats(r.data || null))
      .catch(() => setStats(null));

  useEffect(() => {
    load();
    loadStats();
  }, []);

  const pin = async () => {
    if (!pinFor) return;
    const score = parseInt(pinScore, 10);
    if (Number.isNaN(score) || score < 0 || score > 100) {
      toast.error("Score must be 0-100");
      return;
    }
    setBusy(pinFor + "pin");
    try {
      await apiClient.post(
        `/admin/scans/${pinFor}/pin`,
        { trust_score: score, reason: pinReason },
        wc,
      );
      toast.success(`Pinned ${pinFor} at ${score}/100`);
      setPinFor(null);
      load(search);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Pin failed");
    } finally {
      setBusy(null);
    }
  };

  const unpin = async (domain) => {
    if (!window.confirm(`Remove the verified pin from ${domain}? The AI score will be shown again.`)) return;
    setBusy(domain + "unpin");
    try {
      await apiClient.delete(`/admin/scans/${domain}/pin`, wc);
      toast.success(`Unpinned ${domain}`);
      load(search);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unpin failed");
    } finally {
      setBusy(null);
    }
  };

  const purgeDomain = async (domain) => {
    const typed = window.prompt(
      `This will PERMANENTLY delete every scan, pinned override, and monitor for "${domain}". ` +
        `The public report at /report/${domain} will return 404.\n\n` +
        `Type the domain to confirm:`,
    );
    if (typed == null) return;
    if (typed.trim().toLowerCase() !== domain.toLowerCase()) {
      toast.error("Domain did not match — purge cancelled");
      return;
    }
    setBusy(domain + "purge");
    try {
      const r = await apiClient.delete(`/admin/scans/${domain}/all`, wc);
      toast.success(
        `Deleted ${r.data?.scans_deleted ?? 0} scan(s) and ${r.data?.monitors_deleted ?? 0} monitor(s) for ${domain}`,
      );
      load(search);
      loadStats();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div data-testid="admin-scans">
      <SectionHeader
        kicker="/ Scans"
        title="Verified-score pinning"
        action={
          <div className="flex items-center gap-2 border border-black px-3 py-2 bg-white">
            <Search className="w-4 h-4 text-[#475569]" />
            <input
              data-testid="scans-search"
              className="outline-none text-sm w-56 bg-transparent"
              placeholder="Search domain…"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                load(e.target.value);
              }}
            />
          </div>
        }
      />

      <div className="border border-black bg-white p-4 mb-4 text-sm text-[#475569]">
        <strong className="text-[#0A0A0A]">Why pin?</strong> The AI scorer is
        stochastic &mdash; each scan can drift by &plusmn;5 points because it&apos;s an LLM, not
        a static rubric. For domains you&apos;ve manually audited (especially{" "}
        <code>getprivacylens.com</code>), pinning publishes a stable
        human-verified TrustScore. The badge SVG renders with a {"\u2713"} checkmark and
        the public report carries an &ldquo;is_verified&rdquo; flag. The original AI score
        is preserved as <code>ai_trust_score</code> for transparency.
      </div>

      <TakedownStatsLine stats={stats} />

      {!scans ? (
        <div className="flex items-center gap-2 text-sm text-[#475569]">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading scans…
        </div>
      ) : (
        <div className="border border-black bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F8F9FA] font-mono text-[10px] uppercase tracking-widest text-[#475569]">
              <tr>
                <th className="text-left px-4 py-3">Domain</th>
                <th className="text-right px-4 py-3">AI score</th>
                <th className="text-right px-4 py-3">Pinned</th>
                <th className="text-left px-4 py-3">Pin status</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {scans.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-[#475569]">
                    No scans yet.
                  </td>
                </tr>
              ) : (
                scans.map((s) => (
                  <tr key={s.id} className="border-t border-[#E2E8F0]" data-testid={`scan-row-${s.domain}`}>
                    <td className="px-4 py-3">
                      <a
                        href={`/report/${s.domain}`}
                        target="_blank"
                        rel="noreferrer"
                        className="font-medium hover:text-[#0047FF]"
                      >
                        {s.domain}
                      </a>
                    </td>
                    <td className="px-4 py-3 text-right font-mono">{s.trust_score ?? "—"}</td>
                    <td className="px-4 py-3 text-right font-mono">
                      {s.pinned_trust_score != null ? (
                        <span className="font-display text-lg">{s.pinned_trust_score}</span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {s.pinned_trust_score != null ? (
                        <span
                          className="px-1.5 py-0.5 bg-[#10B981] text-white font-mono text-[10px] uppercase tracking-widest"
                          title={`Pinned by ${s.pinned_by || "?"} · ${s.pinned_reason || ""}`}
                        >
                          ✓ Verified
                        </span>
                      ) : (
                        <span className="text-xs text-[#475569]">AI only</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        <button
                          data-testid={`pin-${s.domain}`}
                          onClick={() => {
                            setPinFor(s.domain);
                            setPinScore(s.pinned_trust_score ?? Math.max(95, s.trust_score ?? 95));
                          }}
                          className="px-2 py-1 text-xs border border-black hover:bg-[#0047FF] hover:text-white"
                        >
                          {s.pinned_trust_score != null ? "Edit pin" : "Pin score"}
                        </button>
                        {s.pinned_trust_score != null && (
                          <button
                            data-testid={`unpin-${s.domain}`}
                            disabled={busy === s.domain + "unpin"}
                            onClick={() => unpin(s.domain)}
                            className="px-2 py-1 text-xs border border-black hover:bg-red-500 hover:text-white"
                          >
                            Unpin
                          </button>
                        )}
                        <button
                          data-testid={`purge-${s.domain}`}
                          disabled={busy === s.domain + "purge"}
                          onClick={() => purgeDomain(s.domain)}
                          className="px-2 py-1 text-xs border border-red-600 text-red-600 hover:bg-red-600 hover:text-white inline-flex items-center gap-1"
                          title="Permanently delete every scan for this domain"
                        >
                          {busy === s.domain + "purge" ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : null}
                          Delete all
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {pinFor && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm grid place-items-center p-4"
          onClick={() => setPinFor(null)}
          data-testid="pin-modal"
        >
          <div
            className="bg-white border border-black w-full max-w-[420px] p-6 pl-hard-shadow-sm"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#0047FF]">
              / Pin verified score
            </div>
            <h3 className="font-display text-2xl tracking-tight mt-1">
              {pinFor}
            </h3>
            <label className="font-mono text-[10px] uppercase tracking-widest text-[#475569] block mt-4 mb-1">
              TrustScore (0–100)
            </label>
            <input
              type="number"
              min={0}
              max={100}
              value={pinScore}
              onChange={(e) => setPinScore(e.target.value)}
              data-testid="pin-score-input"
              className="w-full border border-black px-3 py-2 font-mono text-lg outline-none focus:border-[#0047FF]"
            />
            <label className="font-mono text-[10px] uppercase tracking-widest text-[#475569] block mt-4 mb-1">
              Reason (shown on report tooltip)
            </label>
            <input
              type="text"
              value={pinReason}
              onChange={(e) => setPinReason(e.target.value)}
              data-testid="pin-reason-input"
              className="w-full border border-black px-3 py-2 text-sm outline-none focus:border-[#0047FF]"
            />
            <div className="flex gap-2 mt-5">
              <button
                onClick={pin}
                disabled={busy === pinFor + "pin"}
                data-testid="pin-confirm"
                className="pl-btn-primary px-4 py-2 text-sm border border-black inline-flex items-center gap-2"
              >
                {busy === pinFor + "pin" ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Save pin
              </button>
              <button
                onClick={() => setPinFor(null)}
                className="px-4 py-2 text-sm border border-[#0A0A0A] hover:bg-[#F1F5F9]"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============= PRO DELIVERIES (email send tracking + resend) =============
function ProDeliveries() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("");
  const [busy, setBusy] = useState(null);

  const load = (status = "") =>
    apiClient
      .get("/admin/pro-deliveries", { ...wc, params: { status, limit: 50 } })
      .then((r) => setData(r.data))
      .catch(() => setData({ deliveries: [], totals: {} }));

  useEffect(() => {
    load(filter);
  }, [filter]);

  const resend = async (scanId, domain) => {
    if (!window.confirm(`Resend Pro delivery email for ${domain}?`)) return;
    setBusy(scanId);
    try {
      const r = await apiClient.post(
        `/admin/pro-deliveries/${scanId}/resend`,
        null,
        wc,
      );
      toast.success(`Resent to ${r.data?.email}`);
      load(filter);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Resend failed");
    } finally {
      setBusy(null);
    }
  };

  if (!data) {
    return <Loader2 className="w-5 h-5 animate-spin text-[#0047FF]" />;
  }

  const totals = data.totals || {};
  const rows = data.deliveries || [];

  return (
    <div data-testid="pro-deliveries-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <h2 className="font-display text-2xl tracking-tight">
          Pro deliveries
        </h2>
        <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest">
          {[
            { id: "", label: "All" },
            { id: "sent", label: "Sent" },
            { id: "failed", label: "Failed" },
            { id: "retrying", label: "Retrying" },
            { id: "pending", label: "Pending" },
          ].map((f) => (
            <button
              key={f.id || "all"}
              onClick={() => setFilter(f.id)}
              className={`px-2 py-1 border ${
                filter === f.id
                  ? "bg-[#0A0A0A] text-white border-[#0A0A0A]"
                  : "border-black hover:bg-[#F1F5F9]"
              }`}
              data-testid={`delivery-filter-${f.id || "all"}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div
        className="border border-black bg-[#0A0A0A] text-white px-4 py-3 mb-4 font-mono text-xs uppercase tracking-widest flex flex-wrap items-center gap-x-6 gap-y-1"
        data-testid="delivery-stats"
      >
        <span>
          <span className="text-[#10B981]">{totals.sent || 0}</span> sent
        </span>
        <span>
          <span className="text-[#EF4444]">{totals.failed || 0}</span> failed
        </span>
        <span>
          <span className="text-[#F59E0B]">{totals.pending || 0}</span> pending
        </span>
        <span className="text-[#94A3B8]">
          Click <strong className="text-white">Resend</strong> to retry any
          failed delivery with the full 3-attempt backoff.
        </span>
      </div>

      <div className="border border-black bg-white overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-[#F8F9FA] border-b border-black font-mono uppercase tracking-widest">
            <tr>
              <th className="text-left p-2">Domain</th>
              <th className="text-left p-2">Buyer email</th>
              <th className="text-left p-2">Status</th>
              <th className="text-left p-2">Attempts</th>
              <th className="text-left p-2">Last attempt</th>
              <th className="text-left p-2">Provider</th>
              <th className="text-right p-2">Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={7} className="text-center p-6 text-[#475569]">
                  No deliveries match this filter.
                </td>
              </tr>
            )}
            {rows.map((r) => {
              const status = r.status || "pending";
              const color =
                status === "sent"
                  ? "text-[#065F46] bg-[#D1FAE5]"
                  : status === "failed"
                    ? "text-[#991B1B] bg-[#FEE2E2]"
                    : status === "retrying"
                      ? "text-[#92400E] bg-[#FEF3C7]"
                      : "text-[#1E40AF] bg-[#DBEAFE]";
              return (
                <tr
                  key={r.scan_id}
                  className="border-b border-[#E2E8F0] last:border-0"
                  data-testid={`delivery-row-${r.scan_id}`}
                >
                  <td className="p-2 font-mono">{r.domain}</td>
                  <td className="p-2 break-all">{r.buyer_email || "—"}</td>
                  <td className="p-2">
                    <span className={`px-1.5 py-0.5 ${color} font-mono uppercase tracking-widest`}>
                      {status}
                    </span>
                    {r.last_error && (
                      <div
                        className="text-[10px] text-[#991B1B] mt-1 truncate max-w-[280px]"
                        title={r.last_error}
                      >
                        {r.last_error}
                      </div>
                    )}
                  </td>
                  <td className="p-2">{r.attempts || 0}</td>
                  <td className="p-2 text-[#475569]">
                    {r.last_attempt_at
                      ? new Date(r.last_attempt_at).toLocaleString()
                      : "—"}
                  </td>
                  <td className="p-2 text-[#475569]">{r.provider || "—"}</td>
                  <td className="p-2 text-right">
                    <button
                      disabled={busy === r.scan_id}
                      onClick={() => resend(r.scan_id, r.domain)}
                      className="px-2 py-1 text-xs border border-black hover:bg-[#0047FF] hover:text-white hover:border-[#0047FF] inline-flex items-center gap-1"
                      data-testid={`resend-${r.scan_id}`}
                    >
                      {busy === r.scan_id ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <Send className="w-3 h-3" />
                      )}
                      Resend
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ============= ADMIN SHELL =============
const TABS = [
  { id: "overview", label: "Analytics", icon: BarChart3, component: Overview },
  { id: "users", label: "Users", icon: UsersIcon, component: Users },
  { id: "scans", label: "Scans", icon: ShieldCheck, component: ScansPin },
  { id: "deliveries", label: "Pro deliveries", icon: Send, component: ProDeliveries },
  { id: "payments", label: "Payments", icon: CreditCard, component: Payments },
  { id: "stripe", label: "Stripe", icon: CreditCard, component: StripeSettings },
  { id: "email", label: "SMTP / Email", icon: Mail, component: EmailSettings },
  { id: "llm", label: "LLM Usage", icon: Sparkles, component: LlmUsage },
];

export default function Admin() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [active, setActive] = useState("overview");

  useEffect(() => {
    if (loading) return;
    if (!user) {
      startLogin("/admin");
      return;
    }
    if (!user.is_admin) {
      navigate("/dashboard", { replace: true });
    }
  }, [user, loading, navigate]);

  if (loading || !user) {
    return (
      <main className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20">
        <Loader2 className="w-5 h-5 animate-spin text-[#0047FF]" />
      </main>
    );
  }
  if (!user.is_admin) return null;

  const Active = TABS.find((t) => t.id === active)?.component || Overview;

  return (
    <main className="border-b border-black" data-testid="admin-page">
      <section className="border-b border-black bg-[#0A0A0A] text-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-10">
          <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-[#0047FF] mb-2">
            <ShieldAlert className="w-4 h-4" />
            Admin Console
          </div>
          <h1 className="font-display text-4xl md:text-5xl tracking-tight">
            Operator dashboard
          </h1>
          <p className="text-[#94A3B8] mt-2 text-sm">
            Logged in as <strong className="text-white">{user.email}</strong> ·{" "}
            {new Date().toLocaleString()}
          </p>
        </div>
      </section>

      <section className="border-b border-black bg-white sticky top-16 z-30">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 flex overflow-x-auto">
          {TABS.map((t) => {
            const Icon = t.icon;
            const isActive = active === t.id;
            return (
              <button
                key={t.id}
                data-testid={`admin-tab-${t.id}`}
                onClick={() => setActive(t.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap ${
                  isActive
                    ? "border-[#0047FF] text-[#0047FF]"
                    : "border-transparent text-[#475569] hover:text-[#0A0A0A]"
                }`}
              >
                <Icon className="w-4 h-4" />
                {t.label}
              </button>
            );
          })}
        </div>
      </section>

      <section>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-10">
          <Active />
        </div>
      </section>
    </main>
  );
}
